import { Box, Chip, Divider, List, ListItem } from '@mui/material';
import { RiCalendarLine } from '@remixicon/react';
import { DateTime } from 'luxon';
import { asUTCDate } from 'shared/utils/DateTime';
import { MenuDropDown } from 'src/components/atoms/MenuDropDown';
import LuxonStaticDateRangePicker from './LuxonStaticDateRangePicker';

export const dateRangeShortcuts: { label: string; getRange: () => DateRange; isRange?: boolean }[] = [
  { label: 'This Week', getRange: () => [DateTime.local().startOf('week'), DateTime.local()] },
  {
    label: 'Last Week',
    getRange: () => [
      DateTime.local().minus({ weeks: 1 }).startOf('week'),
      DateTime.local().minus({ weeks: 1 }).endOf('week'),
    ],
  },
  { label: 'Last 7 Days', getRange: () => [DateTime.local().minus({ days: 7 }), DateTime.local()] },
  {
    label: 'Last Month',
    getRange: () => [
      DateTime.local().minus({ months: 1 }).startOf('month'),
      DateTime.local().minus({ months: 1 }).endOf('month'),
    ],
  },
  {
    label: 'Last Quarter',
    getRange: () => [
      DateTime.local().minus({ quarters: 1 }).startOf('quarter'),
      DateTime.local().minus({ quarters: 1 }).endOf('quarter'),
    ],
  },
  { label: 'QTD', getRange: () => [DateTime.local().startOf('quarter'), DateTime.local()], isRange: true },
  { label: 'MTD', getRange: () => [DateTime.local().startOf('month'), DateTime.local()], isRange: true },
  { label: 'YTD', getRange: () => [DateTime.local().startOf('year'), DateTime.local()], isRange: true },
];

export type DateRange = [DateTime, DateTime];

export default function DateRangePicker({
  setDateFilter,
  dateFilter,
  dateFilterLabel,
  showAllTime = false,
  disableRange,
  hint,
  periodPrefix = 'Display',
}: {
  setDateFilter: (dateRange: DateRange, dateRangeLabel?: string) => void;
  dateFilter: DateRange;
  dateFilterLabel?: string;
  showAllTime?: boolean;
  hint?: string;
  disableRange?: boolean;
  periodPrefix?: string;
}) {
  const [from, until] = dateFilter ? dateRangeToStrings(dateFilter) : [];

  let periodDescription = null;
  if (from && until) {
    periodDescription = `${from} thru ${until}`;
  } else if (from) {
    periodDescription = `from ${from}`;
  } else if (until) {
    periodDescription = `thru ${until}`;
  } else {
    periodDescription = `All Time`;
  }
  periodDescription = dateFilterLabel ?? periodDescription;

  const label = disableRange
    ? `${periodPrefix} date (As of ${until ? until : 'today'})`
    : `${periodPrefix} Period (${periodDescription})`;

  return (
    <MenuDropDown icon={<RiCalendarLine size={20} />} hint={hint ?? 'Select date range'} label={label}>
      <Box sx={{ display: 'flex' }}>
        <List
          dense
          sx={(theme) => ({
            p: theme.spacing(2),
            pr: 0,
            '& .MuiListItem-root': { pt: 0, pl: 0, pr: theme.spacing(1) },
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1),
          })}
        >
          {
            // If showAllTime is true, show the "All Time" option
            showAllTime && (
              <ListItem>
                <Chip
                  label="All Time"
                  onClick={() => {
                    setDateFilter([null, null], 'All Time');
                  }}
                />
              </ListItem>
            )
          }
          {dateRangeShortcuts
            .filter((item) => !item.isRange || !disableRange)
            .map((item) => {
              return (
                <ListItem key={item.label}>
                  <Chip
                    label={item.label}
                    onClick={() => {
                      const dateRange = item.getRange() as DateRange;

                      if (disableRange) {
                        setDateFilter([null, dateRange[1]], null);
                      } else {
                        setDateFilter(dateRange, item.label);
                      }
                    }}
                  />
                </ListItem>
              );
            })}
        </List>
        <Divider orientation="vertical" />
        <Box sx={{ my: 'auto' }}>
          <LuxonStaticDateRangePicker
            onChange={([start, end]) => {
              if (disableRange) {
                setDateFilter([null, start], null);
              } else {
                setDateFilter([start, end], null);
              }
            }}
            value={dateFilter}
            calendars={2}
          />
        </Box>
      </Box>
    </MenuDropDown>
  );
}

const dateRangeToStrings = (range: DateRange): [string, string] => {
  const [from, until] = range;

  return [asUTCDate(from)?.toFormat('MM/dd/yyyy'), asUTCDate(until)?.toFormat('MM/dd/yyyy')];
};
